import React from 'react';
import {
  Box, Container, Typography, Button, Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Link } from 'react-router-dom';
import { SERVICES } from '../../data/siteData';
import { revealFromX, useScrollReveal } from '../../hooks/useScrollReveal';

const ServiceItem = ({ service, colIndex, rowIndex, cols, totalItems }) => {
  const isLastCol = colIndex === cols - 1;
  // handle incomplete last row
  const itemIndex = rowIndex * cols + colIndex;
  const isLastRowItem = itemIndex >= totalItems - (totalItems % cols || cols);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: { xs: 3, md: 4 },
        px: { xs: 1, md: 2 },
        borderRight: isLastCol ? 'none' : '1px solid rgba(21,101,192,0.12)',
        borderBottom: isLastRowItem ? 'none' : '1px solid rgba(21,101,192,0.12)',
        transition: 'all 0.3s ease',
        '&:hover .svc-icon': {
          transform: 'translateY(-6px) scale(1.15)',
          filter: 'drop-shadow(0 6px 14px rgba(21,101,192,0.3))',
        },
        '&:hover .svc-title': {
          color: 'primary.main',
        },
      }}
    >
      <Box
        className="svc-icon"
        sx={{
          fontSize: { xs: '2rem', md: '2.4rem' },
          mb: 1.5,
          lineHeight: 1,
          transition: 'transform 0.3s ease, filter 0.3s ease',
        }}
      >
        {service.icon}
      </Box>
      <Typography
        className="svc-title"
        sx={{
          fontFamily: '"DM Sans", sans-serif',
          fontWeight: 600,
          fontSize: { xs: '0.8rem', sm: '0.88rem', md: '0.95rem' },
          color: 'text.primary',
          transition: 'color 0.3s ease',
          lineHeight: 1.3,
        }}
      >
        {service.title}
      </Typography>
    </Box>
  );
};

export default function ServicesSection({ onBookClick, preview = false }) {
  const displayed = preview ? SERVICES.slice(0, 6) : SERVICES;
  const theme = useTheme();
  const COLS = useMediaQuery(theme.breakpoints.up('sm')) ? 3 : 2;;
  const [sectionRef, isVisible] = useScrollReveal();

  return (
    <Box ref={sectionRef} sx={{  bgcolor: '#EBF8FF', overflow:'hidden' }}>
      <Box sx={{ py: { xs: 7, md: 10 } }}>
      <Container maxWidth="xl">

        {/* ── Outer two-column flex row ── */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            gap: { xs: 5, md: 0 },
          }}
        >

          {/* ══ LEFT: text + CTA ══ */}
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: '100%', md: '38%' },
              pr: { md: 6 },
              ...revealFromX(isVisible, 'left'),
            }}
          >
            <Chip
              label="Our Services"
              sx={{
                mb: 2,
                bgcolor: 'rgba(0,201,167,0.1)',
                color: 'secondary.main',
                fontWeight: 600,
                border: '1px solid rgba(0,201,167,0.25)',
              }}
            />

            <Typography
              variant="h2"
              fontWeight={700}
              sx={{
                fontSize: { xs: '1.9rem', md: '2.4rem' },
                lineHeight: 1.2,
                mb: 1,
              }}
            >
              Services{' '}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(135deg, #1565C0, #00C9A7)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                We Offer
              </Box>
            </Typography>

            {/* Accent underline */}
            <Box
              sx={{
                width: 40,
                height: 3,
                borderRadius: 2,
                background: 'linear-gradient(90deg, #1565C0, #00C9A7)',
                mb: 3,
              }}
            />

            <Typography
              variant="body1"
              color="text.secondary"
              mb={2}
              sx={{ fontSize: { xs: '0.9rem', md: '0.95rem' }, lineHeight: 1.8 }}
            >
              From routine check-ups to advanced cosmetic procedures, our expert
              team provides comprehensive dental care tailored to your unique needs
              and comfort.
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              mb={4}
              sx={{ fontSize: { xs: '0.9rem', md: '0.95rem' }, lineHeight: 1.8 }}
            >
              We combine cutting-edge technology with a gentle, patient-first
              approach to deliver results that go beyond expectations.
            </Typography>

            <Button
              variant="contained"
              color="primary"
              component={Link}
              to="/services"
              endIcon={<ArrowForwardIcon />}
              sx={{
                px: 3.5,
                py: 1.2,
                fontWeight: 700,
                letterSpacing: '0.06em',
                fontSize: '0.85rem',
              }}
            >
              VIEW DETAILS
            </Button>
          </Box>

          {/* ══ RIGHT: icon grid ══ */}
          <Box
            sx={{
              flexGrow: 1,
              pl: { md: 0 },
              width: { xs: '100%', md: '62%' },
              // CSS grid with 3 equal columns
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, 1fr)',
                sm: 'repeat(3, 1fr)',
              },
              // outer border wraps the grid
              border: '1px solid rgba(21,101,192,0.12)',
              borderRadius: 2,
              overflow: 'hidden',
              ...revealFromX(isVisible, 'right', 120),
            }}
          >
            {displayed.map((service, idx) => {
              const colIndex = idx % COLS;
              const rowIndex = Math.floor(idx / COLS);
              return (
                <ServiceItem
                  key={service.id}
                  service={service}
                  colIndex={colIndex}
                  rowIndex={rowIndex}
                  cols={COLS}
                  totalItems={displayed.length}
                />
              );
            })}
          </Box>

        </Box>
      </Container>
      </Box>
    </Box>
  );
}

